<template>
  <navigation-layout
    :class="{ '!translate-x-0': isSidebarOpened }"
    :level="2"
    class="translate-x-full duration-200"
    is-sub-layout
    title="Редактировать опрос"
  >
    <template #header>
      <button @click="handleBackClick">
        <img
          alt=""
          class="img--hoar absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 cursor-pointer"
          src="@/assets/icons/direction/arrow-right-long.svg"
        />
      </button>
    </template>

    <template #default>
      <div class="m-1.5">
        <settings-questions-tabs />
      </div>
      <div class="flex gap-1.5 border-y border-y-lunar p-1.5 justify-around">
        <action-button
          v-for="action in filteredEditActions"
          :key="action.value"
          :action="action"
          :active="action.value === EditAction.EDIT"
          :disabled="isPreviewLoading || isStartLoading || isEditLoading"
          :loading="resolveCurrentActionLoading(action.value as EditAction)"
          @click="resolveCurrentAction(action.value as EditAction)"
        />
      </div>
    </template>
  </navigation-layout>
</template>
<script lang="ts" setup>
import { computed, ref } from "vue";
import NavigationLayout from "@/layouts/navigation/navigation-layout.vue";
import { useSidebarStore } from "@/stores/sidebar/sidebar";
import { storeToRefs } from "pinia";
import { SidebarType } from "@/layouts/sidebars/sidebars.types";
import SettingsQuestionsTabs from "@/layouts/sidebars/knowledge-base-create-sidebar/settings-questions-tabs.vue";
import {
  EDIT_ACTIONS,
  EditAction,
} from "@/layouts/sidebars/knowledge-base-create-sidebar/knowledge-testing-survey-sidebar.data";
import ActionButton from "@/modules/knowledge-base/views/testing-survey/components/action-button.vue";
import { useKnowledgeBaseStore } from "@/stores/knowledge-base/knowledge-base";
import { surveyApi } from "@/api";
import { useSnackbarStore } from "@/stores/snackbar/snackbar";
import { useRoute, useRouter } from "vue-router";
import { Route } from "@/router/router.types";
import { QuestionFormStatuses } from "@boostbiz/testing_form_microservice/client/consts";
import type { QuestionFormDTO } from "@boostbiz/question_form_microservice/client/schemas";
import { TestingTab } from "@/modules/knowledge-base/views/testing-survey/testing/tabs/testing-tabs.data";

const router = useRouter();
const route = useRoute();

const { showSnackbar } = useSnackbarStore();

const sidebarStore = useSidebarStore();
const knowledgeBaseStore = useKnowledgeBaseStore();

const { currentSidebars } = storeToRefs(sidebarStore);

const filteredEditActions = computed(() => {
  return knowledgeBaseStore.survey.formStatus === QuestionFormStatuses.active
    ? EDIT_ACTIONS.filter(act => act.value !== EditAction.START)
    : EDIT_ACTIONS
})


const isSidebarOpened = computed(
  () =>
    currentSidebars.value[currentSidebars.value.length - 1] ===
    SidebarType.KNOWLEDGE_BASE_SURVEY_EDIT,
);

const handleBackClick = () => {
  const confirmed = window.confirm("Вы уверены, что хотите завершить редактирование?\nВсе изменения будут утеряны.");

  if (confirmed) {
    sidebarStore.goBack();
    router.push({
      path: `/${Route.KNOWLEDGE_BASE_SURVEY}`,
      query: {
        page: 1,
        count: 10,
        search: "",
      },
    });
  }
};

const isEditLoading = ref(false);
const isPreviewLoading = ref(false);
const isStartLoading = ref(false);

const resolveCurrentActionLoading = (action: EditAction) => {
  switch (action) {
    case EditAction.EDIT:
      return isEditLoading.value;
    case EditAction.START:
      return isStartLoading.value;
    case EditAction.PREVIEW:
      return isPreviewLoading.value;
  }
};

const editSurvey = async () => {
  const shouldValidate =
    knowledgeBaseStore.survey.formStatus !== QuestionFormStatuses.draft;

  if (
    (shouldValidate &&
      knowledgeBaseStore.surveyFormRef!.validate() &&
      knowledgeBaseStore.surveyQuestionsRef!.validate()) ||
    !shouldValidate
  ) {
    isEditLoading.value = true;

    try {
      await surveyApi.actions.patchQuestionForm(
        route.params.id.toString(),
        knowledgeBaseStore.survey,
      );

      showSnackbar("Опрос успешно отредактирован");
      router.push({
        path: `/${Route.KNOWLEDGE_BASE_SURVEY}`,
        query: {
          page: 1,
          count: 10,
          search: "",
        },
      })
    } catch (e) {
      showSnackbar("Не удалось изменить опрос", SnackbarStatus.ERROR);
      console.error(e);
    } finally {
      isEditLoading.value = false;
    }
  }
};

const startSurvey = async () => {
  if (
    knowledgeBaseStore.surveyFormRef!.validate() &&
    knowledgeBaseStore.surveyQuestionsRef!.validate()
  ) {
    isStartLoading.value = true;
    try {
      await surveyApi.actions.startByOwner(knowledgeBaseStore.survey.id);

      showSnackbar("Опрос успешно запущен");
    } catch (e) {
      showSnackbar("Не удалось запустить опрос", SnackbarStatus.ERROR);
      console.error(e);
    } finally {
      isStartLoading.value = false;
    }
  }
};

const previewSurvey = async () => {
  isPreviewLoading.value = true;
  knowledgeBaseStore.surveyPreview =
    knowledgeBaseStore.survey as unknown as QuestionFormDTO;
  await router.push(`/${Route.KNOWLEDGE_BASE_SURVEY_PREVIEW}`);
};

const resolveCurrentAction = (action: EditAction) => {
  switch (action) {
    case EditAction.EDIT:
      return editSurvey();
    case EditAction.START:
      return startSurvey();
    case EditAction.PREVIEW:
      return previewSurvey();
  }
};
</script>

<style lang="scss" scoped></style>
